import axios from 'axios'

import { localStorageHelper } from '../../utils/localStorage.ts'
import {
  ACCESS_TOKEN_LIFETIME,
  BASE_URL,
  REFRESH_THRESHOLD,
  REFRESH_TOKEN_LIFETIME} from '../httpClient.ts'

let refreshPromise: Promise<string> | null = null

export const generateCurrentTimestamp = (): number => {
  return Math.floor(Date.now() / 1000)
}

export const getAccessToken = (): string | null => {
  return localStorageHelper.getItem('accessToken')
}

export const getAccessTokenTimestamp = (): number => {
  return parseInt(localStorageHelper.getItem('accessTokenTimestamp') || '0')
}

export const getRefreshToken = (): string | null => {
  return localStorageHelper.getItem('refreshToken')
}

export const getRefreshTokenTimestamp = (): number => {
  return parseInt(localStorageHelper.getItem('refreshTokenTimestamp') || '0')
}

export const saveAccessToken = (token: string, timestamp: number): void => {
  localStorageHelper.setItem('accessToken', token)
  localStorageHelper.setItem('accessTokenTimestamp', timestamp.toString())
}

export const saveRefreshToken = (token: string, timestamp: number): void => {
  localStorageHelper.setItem('refreshToken', token)
  localStorageHelper.setItem('refreshTokenTimestamp', timestamp.toString())
}

export const isTokenExpiringSoon = (tokenTimestamp: number): boolean => {
  const now = generateCurrentTimestamp()
  return tokenTimestamp + ACCESS_TOKEN_LIFETIME - REFRESH_THRESHOLD <= now
}

export const isRefreshTokenValid = (refreshTokenTimestamp: number): boolean => {
  const now = generateCurrentTimestamp()
  return refreshTokenTimestamp + REFRESH_TOKEN_LIFETIME > now
}

export const refreshAccessToken = async (): Promise<string> => {
  const refreshToken = getRefreshToken()
  const refreshTokenTimestamp = getRefreshTokenTimestamp()

  if (!refreshToken || !isRefreshTokenValid(refreshTokenTimestamp)) {
    throw new Error('Refresh token invalid or expired')
  }

  try {
    const response = await axios.post(
      `${BASE_URL}/rest_framework_simple_jwt/token/refresh/`,
      { refresh: refreshToken }
    )

    const newTokens = response.data
    const currentTimestamp = generateCurrentTimestamp()

    saveAccessToken(newTokens.access, currentTimestamp)

    if (newTokens.refresh) {
      saveRefreshToken(newTokens.refresh, currentTimestamp)
    }

    return newTokens.access
  } catch (error) {
    throw error
  }
}

export const ensureFreshToken = async (): Promise<string> => {
  const accessToken = getAccessToken()
  const accessTokenTimestamp = getAccessTokenTimestamp()

  if (!accessToken || isTokenExpiringSoon(accessTokenTimestamp)) {
    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = refreshAccessToken()

    try {
      const newToken = await refreshPromise
      refreshPromise = null
      return newToken
    } catch (error) {
      refreshPromise = null
      throw error
    }
  }
  return accessToken
}

export const handleTokenRefreshInResponse = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = refreshAccessToken()

  try {
    const newToken = await refreshPromise
    refreshPromise = null
    return newToken
  } catch (error) {
    refreshPromise = null
    throw error
  }
}